<script lang="ts" setup>
import { computed, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { Config } from "datatables.net";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import EngineSeriesRow from "./components/EngineSeriesRow.vue";
import EngineHotRuns from "./components/EngineHotRuns.vue";
import EngineHostWork from "./components/EngineHostWork.vue";
import EngineHostCount from "./components/EngineHostCount.vue";
import {
  useEngineSeries,
  useEngineSeriesCatalogue,
  ENGINE_WINDOW_OPTIONS,
  type AggregationName,
  type EngineSeriesName,
  type EngineSeriesRequest,
} from "@/queries/engine-metrics";

// Every org, so the filter still lists a tenant after the panels have been narrowed to another.
interface OrgListItem {
  orgId: string;
  orgName: string;
}
const { data: orgsData } = useQuery({
  queryKey: ["admin-engine-orgs"],
  queryFn: () => fetchWrapper.get<OrgListItem[]>(`${import.meta.env.VITE_API_URL}/orgs`),
});
const orgs = computed(() => orgsData.value ?? []);
const orgName = (orgId: string) => orgs.value.find((o) => o.orgId === orgId)?.orgName ?? orgId;

const selectedOrgId = ref("");
const serverOrgId = computed(() => selectedOrgId.value || undefined);
const windowIndex = ref(0);
const chosenWindow = computed(() => ENGINE_WINDOW_OPTIONS[windowIndex.value]);

// Which host the page is looking at, or all of them. Set by clicking a row in the hosts table, and it
// narrows every panel below — a host is only interesting once you can see what it is doing.
const selectedInstance = ref("");

const { catalogue } = useEngineSeriesCatalogue();
const describe = (series: EngineSeriesName) => catalogue.value.find((entry) => entry.series === series);

/**
 * The window and the org every panel on this page shares. One control each, not one per chart.
 *
 * The selected host is deliberately not in here. The hosts table is what picks one, so narrowing it to the
 * pick would leave a table of one row and no way back to the others.
 */
function scoped(series: EngineSeriesName, extra: Partial<EngineSeriesRequest>): EngineSeriesRequest {
  return {
    series,
    aggregation: "Rate",
    stepSeconds: chosenWindow.value.stepSeconds,
    minutesBack: chosenWindow.value.minutesBack,
    orgId: selectedOrgId.value || undefined,
    ...extra,
  };
}

// ── Hosts ────────────────────────────────────────────────────────────────────────────────────────────
// Which hosts are up and what each is holding. There is no host document to read this from — no aggregate
// and no heartbeat, since a timer on every instance is not something this solution has — so the answer is
// a measurement split by instance, which every series carries whatever its instrument tagged.
//
// On its own short window rather than the page's, because this table answers "what is up now". Staging
// turned over 471 distinct hosts in two days, so over the page's 7-day window this would be a list of the
// dead, each row showing whatever it last reported. How many were up over a long window is a count, and
// that is the chart beside it.
//
// And outside the page's org filter, which it has to be: a chunk count and the runtime's two meters carry
// no org, so narrowing them to one matches no label and empties the panel. A host is shared infrastructure
// — who it is busy with is the drill underneath.
const PRESENCE = { minutesBack: 15, stepSeconds: 60 };

const onEveryHost = (series: EngineSeriesName, aggregation: AggregationName): EngineSeriesRequest =>
  ({ series, aggregation, splitBy: "Instance", ...PRESENCE });

const chunksRequest = computed(() => onEveryHost("ChunksActive", "Average"));
const cpuRequest = computed(() => onEveryHost("HostCpu", "Rate"));
const memoryRequest = computed(() => onEveryHost("HostMemory", "Max"));

const { lines: chunkLines, seriesLoading: hostsLoading } = useEngineSeries(chunksRequest);
const { lines: cpuLines } = useEngineSeries(cpuRequest);
const { lines: memoryLines } = useEngineSeries(memoryRequest);

/** What the series says now, which for a host is the only reading that answers "is it up". */
const latest = (lines: { label: string; points: { value: number }[] }[], instance: string) =>
  lines.find((line) => line.label === instance)?.points.at(-1)?.value;

/**
 * When anything last reported this host. Shown rather than used to hide a row: a host that stopped four
 * minutes ago is still worth seeing, and its other columns are its last words rather than its current
 * state. Hiding it would make the table lie about a fleet where most hosts live minutes.
 */
const lastSeen = (instance: string) => {
  const times = [chunkLines, cpuLines, memoryLines]
    .flatMap((lines) => lines.value.filter((line) => line.label === instance))
    .map((line) => line.points.at(-1)?.at)
    .filter((at): at is string => Boolean(at))
    .map((at) => new Date(at).getTime());

  return times.length > 0 ? Math.max(...times) : 0;
};

const hosts = computed(() => {
  const instances = new Set(
    [...chunkLines.value, ...cpuLines.value, ...memoryLines.value].map((line) => line.label)
  );

  return [...instances].filter(Boolean).sort().map((instance) => ({
    instance,
    chunks: latest(chunkLines.value, instance) ?? 0,
    cpu: latest(cpuLines.value, instance) ?? 0,
    memory: latest(memoryLines.value, instance) ?? 0,
    lastSeen: lastSeen(instance),
  }));
});

/** How long ago, in the coarsest unit that still says something. */
const ago = (at: number) => {
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  return seconds < 90 ? `${seconds}s ago` : `${Math.round(seconds / 60)}m ago`;
};

const hostColumns = {
  columns: [
    { data: "instance", title: "Host", render: "#hostCell" },
    { data: "chunks", title: "Chunks in hand", render: (value: number) => value.toFixed(0) },
    // Seconds of CPU per second, which is cores — the number that says whether a host is the bottleneck.
    { data: "cpu", title: "Cores used", render: (value: number) => value.toFixed(2) },
    {
      data: "memory",
      title: "Working set",
      render: (value: number) => `${(value / 1024 / 1024).toFixed(0)} MB`,
    },
    { data: "lastSeen", title: "Last seen", render: (value: number) => ago(value) },
  ],
  // No search box, no pager, no row count — there are a handful of hosts and all of them matter.
  layout: { topEnd: "", bottomEnd: "", bottomStart: "" },
} as Config;

// ── Correlation ──────────────────────────────────────────────────────────────────────────────────────
// Rows rather than one chart with several lines: these series are in milliseconds, request units, bytes
// and counts, so a shared Y axis would draw a latency and a charge at comparable heights and mean nothing
// by it. Each row keeps its own axis and they share the time axis, so correlation is read down the column.
const DEFAULT_ROWS: EngineSeriesRequest[] = [
  { series: "Transactions", aggregation: "Rate", splitBy: "TransactionOutcome", stepSeconds: 60, minutesBack: 60 },
  { series: "VendorLatency", aggregation: "P99", splitBy: "None", stepSeconds: 60, minutesBack: 60 },
  { series: "LaneWait", aggregation: "P95", splitBy: "ThrottleReason", stepSeconds: 60, minutesBack: 60 },
];

const rows = ref<EngineSeriesRequest[]>(DEFAULT_ROWS.map((row) => ({ ...row })));

/** The rows as drawn: whatever each row chose, over the window and org the page chose. */
const scopedRows = computed(() =>
  rows.value.map((row) => ({
    ...row,
    stepSeconds: chosenWindow.value.stepSeconds,
    minutesBack: chosenWindow.value.minutesBack,
    orgId: selectedOrgId.value || undefined,
    instanceId: selectedInstance.value || undefined,
  }))
);

const addable = computed(() =>
  catalogue.value.filter((entry) => !rows.value.some((row) => row.series === entry.series))
);

function addRow(series: EngineSeriesName) {
  const entry = describe(series);
  if (!entry) return;

  rows.value = [
    ...rows.value,
    {
      series,
      aggregation: entry.aggregations[0],
      splitBy: "None",
      stepSeconds: chosenWindow.value.stepSeconds,
      minutesBack: chosenWindow.value.minutesBack,
    },
  ];
}

const updateRow = (index: number, request: EngineSeriesRequest) =>
  (rows.value = rows.value.map((row, at) => (at === index ? request : row)));

const removeRow = (index: number) => (rows.value = rows.value.filter((_, at) => at !== index));

// Which step every row is pointing at. Held here rather than in a row, so hovering one row marks the same
// instant on all of them — which is what keeps a read down the column on one column.
const hoverIndex = ref<number | null>(null);
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Engine"
        :breadcrumbs="[{ label: 'Dashboard', href: '/' }, { label: 'Admin' }, { label: 'Engine' }]"
      />

      <p class="text-muted mt-3 mb-4">
        What the engine measured, across every host. Counts and durations only — to see which run or which
        transaction, follow an org into its run history.
      </p>

      <div class="d-flex flex-wrap align-items-end gap-3 mb-4">
        <div style="min-width: 16rem">
          <label class="form-label">Org</label>
          <select v-model="selectedOrgId" class="form-select">
            <option value="">Whole platform</option>
            <option v-for="org in orgs" :key="org.orgId" :value="org.orgId">{{ org.orgName }}</option>
          </select>
        </div>

        <div style="min-width: 11rem">
          <label class="form-label">Window</label>
          <select v-model.number="windowIndex" class="form-select">
            <option v-for="(option, index) in ENGINE_WINDOW_OPTIONS" :key="option.label" :value="index">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- The host narrowing has no control of its own — it is picked by clicking a row — so it needs
             somewhere visible to say it is on, and somewhere to turn it off. -->
        <span v-if="selectedInstance" class="badge bg-tertiary d-inline-flex align-items-center gap-2 py-2">
          Host {{ selectedInstance }}
          <button
            class="btn-close btn-close-sm"
            aria-label="Show every host"
            @click="selectedInstance = ''"
          ></button>
        </span>
      </div>

      <h5 class="mb-2">Hosts</h5>
      <p class="text-muted small mb-3">
        The fleet, whichever org is picked — a chunk count and a host's own CPU carry no tenant, so these
        two panels answer for the whole platform and the drill under them is where an org appears.
      </p>

      <EngineHostCount
        :minutes-back="chosenWindow.minutesBack"
        :step-seconds="chosenWindow.stepSeconds"
        class="mb-4"
      />

      <h6 class="mb-1">Up now</h6>
      <p class="text-muted small mb-3">
        One row per host that reported in the last quarter hour — its own window, not the one above, because
        this is what is running rather than what ran. Chunks in hand is also how many lanes that host holds:
        one chunk holds one lane for its whole time. Pick a host to narrow everything below it.
      </p>

      <DataView :data="hosts" :view-mode="'table'" :options="hostColumns">
        <template #hostCell="slotProps">
          <a href="#" @click.prevent="selectedInstance = slotProps.rowData.instance">
            {{ slotProps.rowData.instance }}
          </a>
        </template>

        <template #empty-state>
          <div class="text-muted small py-4 text-center">
            {{
              hostsLoading
                ? "Reading…"
                : "No host reported in this window. Either nothing ran, or this deployment stores no metrics."
            }}
          </div>
        </template>
      </DataView>

      <div v-if="selectedInstance" class="mt-4 pt-3 border-top">
        <EngineHostWork
          :instance-id="selectedInstance"
          :org-name="orgName"
          :minutes-back="chosenWindow.minutesBack"
          :step-seconds="chosenWindow.stepSeconds"
          @select="(orgId: string) => (selectedOrgId = orgId)"
        />
      </div>
    </PanelCard>

    <!-- Read from the run documents rather than from metrics, so this panel answers even where no metrics
         store is configured — and it is where a chart's spike gets a name. -->
    <PanelCard>
      <EngineHotRuns :org-id="serverOrgId" :org-name="orgName" />
    </PanelCard>

    <PanelCard>
      <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h5 class="mb-0">Correlation</h5>

        <div class="dropdown ms-auto">
          <button class="bb-btn-secondary-cta btn-sm dropdown-toggle" data-bs-toggle="dropdown">
            <i class="bi bi-plus me-1"></i>Add series
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li v-for="entry in addable" :key="entry.series">
              <a class="dropdown-item" href="#" @click.prevent="addRow(entry.series)">
                {{ entry.series }}
              </a>
            </li>
            <li v-if="addable.length === 0">
              <span class="dropdown-item-text text-muted small">Every series is already shown.</span>
            </li>
          </ul>
        </div>
      </div>

      <p class="text-muted small mb-3">
        Each series keeps its own axis, because milliseconds and request units are not comparable heights.
        They share the time axis, so read a correlation down the column rather than across one chart.
      </p>

      <div class="d-flex flex-column gap-3">
        <EngineSeriesRow
          v-for="(row, index) in scopedRows"
          :key="`${row.series}-${index}`"
          :request="row"
          :description="describe(row.series)"
          :removable="scopedRows.length > 1"
          :hover-index="hoverIndex"
          @update="(updated) => updateRow(index, updated)"
          @remove="removeRow(index)"
          @hover="(at) => (hoverIndex = at)"
        />
      </div>
    </PanelCard>
  </PanelGrid>
</template>
